import { roleNameFromRoleType } from '@euler/functions/user/util';
import {
  CoachingCaseBrowseRequest,
  CoachingCaseFormat,
  CoachingCaseLength,
  CoachingChannel,
  CoachingLibraryCatalogue,
  CoachingNamedItem,
  CoachingOpeningSide,
  CoachingRelationshipStage,
  kCoachingDifficulties,
} from '@euler/model/coaching';
import { OrgUserRoleType } from '@euler/model/enum';
import {
  kChannelNames,
  kFormatNames,
  kLevelNames,
  kOpeningNames,
} from '../copy';

/** The catalog filters other than search, category and collection. */
export type CaseFilterKey =
  | 'difficulties'
  | 'formats'
  | 'channels'
  | 'relationshipStages'
  | 'openingSides'
  | 'roles'
  | 'criteria'
  | 'tags'
  | 'lengths';

const kCaseFilterKeys: CaseFilterKey[] = [
  'difficulties',
  'formats',
  'channels',
  'relationshipStages',
  'openingSides',
  'roles',
  'criteria',
  'tags',
  'lengths',
];

export type CaseFilterValue = string | number;

export interface CaseFilterOption {
  value: CaseFilterValue;
  label: string;
}

/** One filter as the sheet shows it, in the sheet's order. */
export interface CaseFilterSection {
  key: CaseFilterKey;
  title: string;
  /** Its options; titled groups when the filter has parts (练习能力). */
  groups: { title: string | null; options: CaseFilterOption[] }[];
  /** Set apart from the filters above it. */
  divided: boolean;
}

const kStages: CaseFilterOption[] = [
  { value: CoachingRelationshipStage.Prospective, label: '潜在客户' },
  { value: CoachingRelationshipStage.FirstTime, label: '首次到店' },
  { value: CoachingRelationshipStage.Active, label: '老客户' },
  { value: CoachingRelationshipStage.AtRisk, label: '有流失风险' },
  { value: CoachingRelationshipStage.Inactive, label: '久未到店' },
  { value: CoachingRelationshipStage.Returning, label: '回流客户' },
];

const kLengths: CaseFilterOption[] = [
  { value: CoachingCaseLength.Short, label: '短（6 个来回以内）' },
  { value: CoachingCaseLength.Medium, label: '中' },
  { value: CoachingCaseLength.Long, label: '长（12 个来回以上）' },
];

/** The roles the library's cases are written for. */
const kRoles: OrgUserRoleType[] = [
  OrgUserRoleType.ServiceAgents,
  OrgUserRoleType.Manager,
  OrgUserRoleType.Marketer,
  OrgUserRoleType.Quoter,
  OrgUserRoleType.Technicians,
];

function plain(
  key: CaseFilterKey,
  title: string,
  options: CaseFilterOption[],
  divided = false,
): CaseFilterSection {
  return { key, title, groups: [{ title: null, options }], divided };
}

/**
 * Every filter with its options. A selected tag outside the catalogue's
 * most used ones (picked from a case) is listed first, by `knownTags`.
 */
export function caseFilterSections(
  catalogue: CoachingLibraryCatalogue | null,
  selectedTags: readonly string[],
  knownTags: readonly CoachingNamedItem[],
): CaseFilterSection[] {
  const sections = [
    plain(
      'difficulties',
      '难度',
      kCoachingDifficulties.map(value => ({
        value,
        label: kLevelNames[value],
      })),
    ),
    plain(
      'formats',
      '形式',
      Object.values(CoachingCaseFormat).map(value => ({
        value,
        label: kFormatNames[value],
      })),
    ),
    plain(
      'channels',
      '场合',
      Object.values(CoachingChannel).map(value => ({
        value,
        label: kChannelNames[value],
      })),
    ),
    plain('relationshipStages', '客户关系', kStages),
    plain(
      'openingSides',
      '谁先开口',
      Object.values(CoachingOpeningSide).map(value => ({
        value,
        label: kOpeningNames[value],
      })),
    ),
    plain(
      'roles',
      '适合岗位',
      kRoles.map(value => ({ value, label: roleNameFromRoleType(value) })),
    ),
  ];
  if (catalogue?.criterionAreas.length)
    sections.push({
      key: 'criteria',
      title: '练习能力',
      groups: catalogue.criterionAreas.map(area => ({
        title: area.name,
        options: area.criteria.map(item => ({
          value: item.slug,
          label: item.name,
        })),
      })),
      divided: true,
    });
  const offered = catalogue?.tags ?? [];
  const extra = knownTags.filter(
    tag =>
      selectedTags.includes(tag.slug) &&
      !offered.some(item => item.slug === tag.slug),
  );
  if (extra.length || offered.length)
    sections.push(
      plain(
        'tags',
        '标签',
        [...extra, ...offered].map(tag => ({
          value: tag.slug,
          label: tag.name,
        })),
        true,
      ),
    );
  sections.push(plain('lengths', '时长', kLengths, true));
  return sections;
}

/** How many filter values are selected. */
export function caseFilterCount(request: CoachingCaseBrowseRequest): number {
  return kCaseFilterKeys.reduce(
    (sum, key) => sum + (request[key]?.length ?? 0),
    0,
  );
}

/** Switches one value of a filter on or off. */
export function toggleCaseFilter(
  request: CoachingCaseBrowseRequest,
  key: CaseFilterKey,
  value: CaseFilterValue,
): CoachingCaseBrowseRequest {
  const values = (request[key] ?? []) as CaseFilterValue[];
  const next = values.includes(value)
    ? values.filter(item => item !== value)
    : [...values, value];
  return { ...request, [key]: next.length ? next : undefined };
}

/** The request without filters: search, category and collection stay. */
export function clearCaseFilters(
  request: CoachingCaseBrowseRequest,
): CoachingCaseBrowseRequest {
  return {
    keyword: request.keyword,
    category: request.category,
    collection: request.collection,
  };
}

/** `request` with the filters of `from` in place of its own. */
export function withCaseFiltersOf(
  request: CoachingCaseBrowseRequest,
  from: CoachingCaseBrowseRequest,
): CoachingCaseBrowseRequest {
  const next = clearCaseFilters(request);
  for (const key of kCaseFilterKeys)
    if (from[key]?.length) Object.assign(next, { [key]: from[key] });
  return next;
}

/** A filter's selected values, labelled, in the sheet's order. */
export interface SelectedCaseFilter {
  key: CaseFilterKey;
  title: string;
  options: CaseFilterOption[];
}

export function selectedCaseFilters(
  request: CoachingCaseBrowseRequest,
  sections: CaseFilterSection[],
): SelectedCaseFilter[] {
  return sections.flatMap(section => {
    const selected = (request[section.key] ?? []) as CaseFilterValue[];
    if (!selected.length) return [];
    const options = section.groups.flatMap(group => group.options);
    return [
      {
        key: section.key,
        title: section.title,
        // A value the options no longer name still shows, and can be removed.
        options: selected.map(
          value =>
            options.find(option => option.value === value) ?? {
              value,
              label: String(value),
            },
        ),
      },
    ];
  });
}
